import React, { useState } from "react";
import { Modal } from "react-bootstrap";
import apiService from "../../../services/apiService";
import logger from "../../../utils/logger";
import { showApiMessage } from "../../../utils/messageHandler";
import { useAnalytics } from "../../../components/Analytics";
import { AnalyticsEvents, AnalyticsProps } from "../../../utils/analytics";

const DangerZone = () => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { trackEvent } = useAnalytics();

  const currentUserDetails = () => {
    try {
      const jsonStr = localStorage.getItem("currentUser");
      if (!jsonStr) {
        logger.log("No user data in localStorage");
        return null;
      }

      const userDetails = JSON.parse(jsonStr);
      if (!userDetails || !userDetails.id) {
        logger.log("Invalid user data in localStorage:", userDetails);
        return null;
      }

      return userDetails;
    } catch (error) {
      logger.error("Error parsing user data from localStorage:", error);
      return null;
    }
  };

  const isAdminUser = () => {
    const currentUser = currentUserDetails();
    return currentUser?.role === "ROLE_ADMINISTRATOR";
  };

  const showDeleteConfirmation = () => {
    setShowDeleteModal(true);

    // Track delete account initiated event - without user-specific info
    trackEvent(AnalyticsEvents.DELETE_ACCOUNT_INITIATED, {
      [AnalyticsProps.ACTION_TYPE]: "open_delete_modal",
      modal_opened: true,
    });
  };

  const handleDeleteAccount = async () => {
    try {
      setIsDeleting(true);

      // Track delete account confirmation - without user-specific info
      trackEvent(AnalyticsEvents.DELETE_ACCOUNT_INITIATED, {
        [AnalyticsProps.ACTION_TYPE]: "confirm_delete",
        confirmation_clicked: true,
      });

      const response = await apiService.deleteUserAccount();

      if (
        response.status === 200 ||
        response.status === 204 ||
        (response.status === 401 && response.data?.message?.includes("Token has expired"))
      ) {
        // Track successful account deletion - without user-specific info
        trackEvent(AnalyticsEvents.DELETE_ACCOUNT_SUCCESS, {
          immediate_deletion: true,
        });

        // Clear storage and redirect for both successful deletion and token expiration
        localStorage.clear();
        window.location.href = "/signin";
      } else if (response.data?.message?.includes("pending admin approval")) {
        // Track pending approval deletion - without user-specific info
        trackEvent(AnalyticsEvents.DELETE_ACCOUNT_SUCCESS, {
          pending_approval: true,
        });

        // Show pending approval message
        const syntheticSuccess = {
          status: 200,
          data: {
            message: "Your request to delete your account is pending admin approval.",
          },
        };
        showApiMessage(syntheticSuccess, "delete account");
        setShowDeleteModal(false);
      } else {
        // Track failed account deletion - without user-specific info
        trackEvent(AnalyticsEvents.DELETE_ACCOUNT_FAILED, {
          [AnalyticsProps.ERROR_REASON]: "unknown_error",
          response_status: response.status,
          response_message: response.data?.message || "Unknown error",
        });

        const syntheticError = {
          response: {
            status: response?.status || 400,
            data: { message: "Failed to delete account. Please try again." },
          },
        };
        showApiMessage(syntheticError, "delete account");
      }
    } catch (error) {
      logger.error("Error deleting account:", error);

      // Track error in account deletion - without user-specific info
      trackEvent(AnalyticsEvents.DELETE_ACCOUNT_FAILED, {
        [AnalyticsProps.ERROR_REASON]: error.message || "api_error",
        [AnalyticsProps.ERROR_MESSAGE]: JSON.stringify(error.response?.data || {}),
      });

      showApiMessage(error, "delete account");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div>
      <p>&nbsp;</p>
      <h4>Danger Zone</h4>

      <div className="mt-4">
        {!isAdminUser() && (
          <div className="card border-danger">
            <div className="card-header bg-danger text-white">
              <h5 className="mb-0">Delete Account</h5>
            </div>
            <div className="card-body">
              <p className="card-text text-danger mb-4">
                <strong>Warning:</strong> Deleting your account is permanent and cannot be reversed. All your data will
                be permanently removed from our system.
              </p>
              <button
                type="button"
                className="btn btn-outline-danger"
                style={{
                  padding: "8px 24px",
                  fontSize: "14px",
                  border: "1px solid #dc3545",
                  width: "160px",
                }}
                onClick={showDeleteConfirmation}
              >
                Delete Account
              </button>
            </div>
          </div>
        )}
      </div>

      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        centered
        style={{
          borderRadius: "30px",
        }}
      >
        <Modal.Header
          style={{
            border: "none",
            padding: "15px 15px 0 15px",
            display: "flex",
            justifyContent: "flex-end",
            minHeight: "40px",
          }}
        >
          <button
            className="btn-close"
            onClick={() => setShowDeleteModal(false)}
            style={{
              background: "none",
              border: "none",
              fontSize: "20px",
              color: "#000",
              padding: "0",
              margin: "0",
              opacity: 0.5,
              cursor: "pointer",
              position: "absolute",
              right: "15px",
              top: "10px",
            }}
            aria-label="Close"
          >
            ×
          </button>
        </Modal.Header>
        <Modal.Body
          className="text-center"
          style={{
            padding: "5px 20px 24px 20px",
          }}
        >
          <h5
            className="text-primary"
            style={{
              fontSize: "18px",
              marginTop: "10px",
              marginBottom: "24px",
              fontWeight: "normal",
            }}
          >
            You are about to delete your account
          </h5>
          <div className="d-flex justify-content-center gap-3">
            <button
              className="btn btn-outline-primary"
              style={{
                borderRadius: "20px",
                width: "8rem",
              }}
              onClick={() => setShowDeleteModal(false)}
              disabled={isDeleting}
            >
              Cancel
            </button>
            <button
              className="btn btn-outline-primary"
              style={{
                borderRadius: "20px",
                width: "8rem",
              }}
              onClick={handleDeleteAccount}
              disabled={isDeleting}
            >
              {isDeleting ? (
                <div className="spinner-border spinner-border-sm" role="status">
                  <span className="visually-hidden">Deleting...</span>
                </div>
              ) : (
                "Confirm"
              )}
            </button>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default DangerZone;
